// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Alert,
  Button,
  Drawer,
  Group,
  Loader,
  Pagination,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { getReferenceString } from '@medplum/core';
import type { Appointment, Practitioner, Task } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { IconAlertCircle, IconPlus, IconSearch } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { getIncompleteNoteDetails, IncompleteNotesTable } from '../components/dashboard/IncompleteNotesTable';
import { AppointmentDetailsModal } from '../components/schedule/AppointmentDetailsModal';
import { AppointmentsCalendarCard } from '../components/schedule/AppointmentsCalendarCard';
import { CreateVisit } from '../components/schedule/CreateVisit';
import { SelectPatientDrawer } from '../components/schedule/SelectPatientDrawer';
import { NewTaskModal } from '../components/tasks/NewTaskModal';
import { TaskCard } from '../components/tasks/TaskCard';
import { useAppointmentDetails } from '../hooks/useAppointmentDetails';
import { useProviderAppointmentDetails } from '../hooks/useProviderAppointmentDetails';
import { useProviderAppointments } from '../hooks/useProviderAppointments';
import { useRole } from '../hooks/useRole';
import { canViewClinicAppointments } from '../utils/permissions';
import classes from './DashboardPage.module.css';

const TASK_OPEN_STATUSES = 'draft,requested,ready,received,accepted,in-progress';
const TERMINAL_TASK_STATUSES: readonly Task['status'][] = ['completed', 'cancelled', 'rejected', 'failed'];
const TASKS_PER_PAGE = 4;

// Mirror the polaris-themed scrollbar used by SchedulePage's tables so the
// dashboard's horizontal task rows don't fall back to the OS default bar.
const taskScrollAreaStyles = {
  thumb: { backgroundColor: 'var(--mantine-color-polaris-5)' },
};

export function DashboardPage(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const navigate = useNavigate();

  // Appointments (today + recent fulfilled for incomplete notes)
  const { role, isAdmin } = useRole();
  const clinicViewEnabled = canViewClinicAppointments({ role, isAdmin });
  const { recentAppointments } = useProviderAppointments({ clinicView: clinicViewEnabled });
  const { details: recentDetails, loading: recentLoading } = useProviderAppointmentDetails(recentAppointments);

  // Section search inputs
  const [notesSearch, setNotesSearch] = useState('');
  const [debouncedNotesSearch] = useDebouncedValue(notesSearch, 200);
  const [tasksSearch, setTasksSearch] = useState('');
  const [debouncedTasksSearch] = useDebouncedValue(tasksSearch, 200);

  // Banner dismissal (per-session only; resets on reload)
  const [notesBannerDismissed, setNotesBannerDismissed] = useState(false);
  const [tasksBannerDismissed, setTasksBannerDismissed] = useState(false);

  // Tasks
  const [myTasks, setMyTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);

  // Calendar modal/drawer state
  const [createAppointmentOpened, createAppointmentHandlers] = useDisclosure(false);
  const [patientPickerOpened, patientPickerHandlers] = useDisclosure(false);
  const [appointmentSlot, setAppointmentSlot] = useState<unknown>();
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | undefined>();
  const [calendarRefreshToken, setCalendarRefreshToken] = useState(0);
  const [appointmentDetailsOpened, appointmentDetailsHandlers] = useDisclosure(false);
  const selectedAppointmentList = useMemo(
    () => (selectedAppointment ? [selectedAppointment] : []),
    [selectedAppointment]
  );
  const { details: selectedAppointmentDetails } = useAppointmentDetails(selectedAppointmentList);

  // Modal state
  const [taskModalOpened, taskModalHandlers] = useDisclosure(false);
  const [editingTask, setEditingTask] = useState<Task | undefined>();

  // Card pagination
  const [myTasksPage, setMyTasksPage] = useState(1);

  const profileRef = profile ? getReferenceString(profile) : undefined;

  const fetchTasks = useCallback(async () => {
    if (!profileRef) {
      return;
    }
    setTasksLoading(true);
    try {
      const mine = await medplum.searchResources('Task', {
        owner: profileRef,
        status: TASK_OPEN_STATUSES,
        _count: '100',
        _sort: '-_lastUpdated',
      });
      setMyTasks(mine);
    } catch (err) {
      console.error('Failed to load dashboard tasks', err);
    } finally {
      setTasksLoading(false);
    }
  }, [medplum, profileRef]);

  useEffect(() => {
    fetchTasks().catch(console.error);
  }, [fetchTasks]);

  const incompleteNotesCount = useMemo(
    () => getIncompleteNoteDetails(recentDetails, profile?.id).length,
    [profile?.id, recentDetails]
  );

  const overdueTasksCount = useMemo(() => {
    const now = Date.now();
    return myTasks.filter((t) => {
      const due = t.restriction?.period?.end;
      if (!due) {
        return false;
      }
      if (t.status && TERMINAL_TASK_STATUSES.includes(t.status)) {
        return false;
      }
      return new Date(due).getTime() < now;
    }).length;
  }, [myTasks]);

  const filteredMyTasks = useMemo(() => filterTasks(myTasks, debouncedTasksSearch), [myTasks, debouncedTasksSearch]);

  const myTasksTotalPages = Math.max(1, Math.ceil(filteredMyTasks.length / TASKS_PER_PAGE));
  const myTasksPaginated = filteredMyTasks.slice((myTasksPage - 1) * TASKS_PER_PAGE, myTasksPage * TASKS_PER_PAGE);

  const handleTaskCreated = (): void => {
    fetchTasks().catch(console.error);
  };

  const handleTaskUpdated = (): void => {
    fetchTasks().catch(console.error);
    setEditingTask(undefined);
  };

  const handleEdit = (task: Task): void => {
    setEditingTask(task);
    taskModalHandlers.open();
  };

  const handleResolved = (): void => {
    fetchTasks().catch(console.error);
  };

  const handleCloseModal = (): void => {
    taskModalHandlers.close();
    setEditingTask(undefined);
  };

  const handleCreateTaskClick = (): void => {
    setEditingTask(undefined);
    taskModalHandlers.open();
  };

  return (
    <Stack gap="md" p="md">
      {!notesBannerDismissed && incompleteNotesCount > 0 && (
        <Alert
          className={`${classes.dashboardAlert} ${classes.notesAlert}`}
          icon={<IconAlertCircle />}
          title="Outstanding Notes"
          withCloseButton
          onClose={() => setNotesBannerDismissed(true)}
        >
          You have <strong>{incompleteNotesCount}</strong> incomplete clinical{' '}
          {incompleteNotesCount === 1 ? 'note' : 'notes'}.
        </Alert>
      )}

      {!tasksBannerDismissed && overdueTasksCount > 0 && (
        <Alert
          className={`${classes.dashboardAlert} ${classes.tasksAlert}`}
          icon={<IconAlertCircle />}
          title="Outstanding Tasks"
          withCloseButton
          onClose={() => setTasksBannerDismissed(true)}
        >
          You have <strong>{overdueTasksCount}</strong> outstanding {overdueTasksCount === 1 ? 'task' : 'tasks'}.
        </Alert>
      )}

      <Paper className={classes.hero} radius="md">
        <Title order={1} size="h2" fw={700} c="white">
          Dashboard
        </Title>
      </Paper>

      <AppointmentsCalendarCard
        title={clinicViewEnabled ? 'Clinic Calendar' : 'My Calendar'}
        canViewClinicAppointments={clinicViewEnabled}
        includeAvailability
        showCreateAppointment
        onCreateAppointment={() => patientPickerHandlers.open()}
        showEditAvailability
        onEditAvailability={() => navigate('/settings#availability')}
        onAppointmentSelect={(appointment) => {
          setSelectedAppointment(appointment);
          appointmentDetailsHandlers.open();
        }}
        refreshToken={calendarRefreshToken}
        onSlotSelect={(slot) => {
          createAppointmentHandlers.open();
          setAppointmentSlot(slot);
        }}
        calendarHeight={700}
      />

      <SelectPatientDrawer
        opened={patientPickerOpened}
        onClose={patientPickerHandlers.close}
        onPatientSelect={(patient) => {
          patientPickerHandlers.close();
          if (patient.id) {
            navigate(`/Patient/${patient.id}/Appointment/schedule`);
          }
        }}
      />

      <Drawer
        opened={createAppointmentOpened}
        onClose={() => {
          createAppointmentHandlers.close();
          setAppointmentSlot(undefined);
        }}
        title="New Calendar Event"
        position="right"
        h="100%"
      >
        <CreateVisit appointmentSlot={appointmentSlot as any} />
      </Drawer>

      {selectedAppointmentDetails[0] && (
        <AppointmentDetailsModal
          appointment={selectedAppointmentDetails[0]}
          opened={appointmentDetailsOpened}
          onCancelled={() => setCalendarRefreshToken((token) => token + 1)}
          onClose={() => {
            appointmentDetailsHandlers.close();
            setSelectedAppointment(undefined);
          }}
        />
      )}

      {(recentLoading || incompleteNotesCount > 0) && (
        <Paper withBorder shadow="sm" radius="md" p="lg">
          <Group justify="space-between" mb="md" wrap="wrap">
            <Title order={3}>Incomplete Notes</Title>
            <TextInput
              placeholder="Search"
              value={notesSearch}
              onChange={(e) => setNotesSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
              size="sm"
            />
          </Group>
          <ScrollArea>
            <IncompleteNotesTable
              appointments={recentDetails}
              currentProviderId={profile?.id}
              loading={recentLoading}
              searchQuery={debouncedNotesSearch}
              showProvider={false}
            />
          </ScrollArea>
        </Paper>
      )}

      <Paper withBorder shadow="sm" radius="md" p="lg">
        <Group justify="space-between" mb="md" wrap="wrap">
          <Title order={3}>Open Tasks</Title>
          <Group gap="sm" wrap="nowrap">
            <TextInput
              placeholder="Search"
              value={tasksSearch}
              onChange={(e) => setTasksSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
              size="sm"
            />
            <Button leftSection={<IconPlus size={14} />} onClick={handleCreateTaskClick}>
              Create Task
            </Button>
          </Group>
        </Group>

        <Stack gap="lg">
          <TaskRow
            label="Tasks for You"
            tasks={myTasksPaginated}
            allTasks={filteredMyTasks}
            loading={tasksLoading}
            page={myTasksPage}
            totalPages={myTasksTotalPages}
            onPageChange={setMyTasksPage}
            variant="mine"
            emptyMessage="No open tasks assigned to you."
            onEdit={handleEdit}
            onResolved={handleResolved}
          />
        </Stack>
      </Paper>

      <NewTaskModal
        opened={taskModalOpened}
        onClose={handleCloseModal}
        onTaskCreated={handleTaskCreated}
        onTaskUpdated={handleTaskUpdated}
        task={editingTask}
      />
    </Stack>
  );
}

interface TaskRowProps {
  label: string;
  tasks: Task[];
  allTasks: Task[];
  loading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  variant: 'mine' | 'assigned';
  emptyMessage: string;
  onEdit: (task: Task) => void;
  onResolved: () => void;
}

function TaskRow(props: TaskRowProps): JSX.Element {
  const { label, tasks, allTasks, loading, page, totalPages, onPageChange, variant, emptyMessage, onEdit, onResolved } =
    props;

  let body: JSX.Element;
  if (loading) {
    body = (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  } else if (tasks.length === 0) {
    body = (
      <Text c="dimmed" size="sm" py="sm">
        {emptyMessage}
      </Text>
    );
  } else {
    body = (
      <ScrollArea scrollbarSize={6} type="auto" styles={taskScrollAreaStyles}>
        <Group gap="md" align="stretch" wrap="nowrap" pb="xl">
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} variant={variant} onEdit={onEdit} onResolved={onResolved} />
          ))}
        </Group>
      </ScrollArea>
    );
  }

  return (
    <Stack gap="sm">
      <Text fw={600}>{label}</Text>
      {body}
      {allTasks.length > 0 && totalPages > 1 && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Page {page} of {totalPages} • {allTasks.length} total
          </Text>
          <Pagination value={page} onChange={onPageChange} total={totalPages} size="sm" />
        </Group>
      )}
    </Stack>
  );
}

function filterTasks(tasks: Task[], query: string): Task[] {
  if (!query.trim()) {
    return tasks;
  }
  const q = query.toLowerCase();
  return tasks.filter((t) => {
    const title = t.code?.text ?? '';
    const desc = t.description ?? '';
    return title.toLowerCase().includes(q) || desc.toLowerCase().includes(q);
  });
}
